/*----------------------------------
- ROUTE: /newsletter
----------------------------------*/

/**
 * @docs docs/features/newsletter
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { definePageRoute } from '@common/router/definitions';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { NEWSLETTER_PAGE_CONTENT } from '@/client/catalogs/landing/newsletter/pageContent';
import { PUBLIC_INDEX_ROBOTS_DIRECTIVES } from '@/common/catalogs/crawlerPolicy';
import {
    buildNewsletterStructuredData,
    type WebsiteJsonLdNode,
} from '@/client/pages/landing/seo/structuredData';
import NewsletterPage from './page/NewsletterPage';
import { cachedLandingHeaderNavigation } from '@/common/libs/connected/publicProductReadCache';

/*----------------------------------
- ROUTES
----------------------------------*/

export default definePageRoute({
    path: '/newsletter',
    options: {
        auth: false,
        layout: false,
        static: {
            refresh: '1 day',
            urls: ['/newsletter'],
        },
    },
    data: ({ Product }) => ({
        navigation: cachedLandingHeaderNavigation.read() ?? Product.Navigation.GetLandingHeaderNavigation(),
    }),
    render: ({
        page,
        Router,
        navigation,
    }: {
        page: {
            description?: string;
            head: Array<{ $: string; [key: string]: string | Date | null | undefined }>;
            jsonld: WebsiteJsonLdNode[];
            title?: string;
            url: string;
        };
        Router: { url: (path: string) => string };
        navigation: LandingHeaderDomainsNavigation;
    }) => {
        const content = NEWSLETTER_PAGE_CONTENT;
        const canonicalUrl = Router.url('/newsletter');

        page.title = content.meta.pageTitle;
        page.description = content.meta.metaDescription;
        page.url = canonicalUrl;
        page.head.push({ $: 'meta', name: 'robots', content: PUBLIC_INDEX_ROBOTS_DIRECTIVES });
        page.head.push({
            $: 'link',
            rel: 'alternate',
            type: 'text/markdown',
            href: Router.url('/ai/pages/newsletter.md'),
        });
        page.jsonld.push(
            ...buildNewsletterStructuredData({
                canonicalUrl,
                description: content.meta.metaDescription,
                sends: content.schedule.items.map((item) => ({
                    name: item.tag,
                    cadence: item.cadence,
                })),
                title: content.meta.pageTitle,
            }),
        );

        return <NewsletterPage navigation={navigation} />;
    },
});
